<i18n>
{
    "uk": {
        "publishDate": "Дата публікації",
        "moreByAuthor": {
            "heading": "Інші новини компанії",
            "link": "Всі новини"
        }
    },
    "en": {
        "publishDate": "Publish date",
        "moreByAuthor": {
            "heading": "More company news",
            "link": "See all"
        }
    }
}
</i18n>

<template>
<section>
    <section class="page-container transition-up pb-6 pb-md-12 pt-2 pt-md-5 px-0 px-md-4">
        <NavigationBar>
            <template v-slot:left>
                <BackButton 
                    :to="localePath('/articles')"
                    class="layer-2" 
                />
            </template>

            <template v-slot:center>
                <div :class="{'show': !titleVisible}" class="nav-bar-title">
                    <div class="max-lines-1 f-semismall fw-700">
                        {{  article.title }}
                    </div>
                    <nuxt-link :to="localePath(`/${author.slug}`)" class="f-small g-link max-lines-1">
                        {{ author.displayName || author.name }}
                    </nuxt-link>
                </div>
            </template>

            <template v-slot:right >
                <div class="d-flex">
                    <ShareButton
                        class="btn-bg btn-round layer-2" 
                        id="share-post-mobile"
                        anchor="share-post-mobile"
                        :title="$t('general.share')"
                    >
                        <span class="icon-16 icon-ic-share" /> 
                    </ShareButton>


                    <nuxt-link
                        v-if="canEdit"
                        :to="localePath(`/editor/${article.id}`)"
                        class="btn-primary btn-round ml-1"
                        :title="$t('general.actions')"
                    >
                        <span class="icon-16 icon-ic-pencil-outline" />
                    </nuxt-link>
                </div>
            </template>
        </NavigationBar>

        <div class="row no-nmd-gutter">
            <div class="col-lg-8" id="left-side">
                <div class="article-body mx-2 mx-sm-auto mx-lg-0">
                    <h1 class="selectable h3 h2-md h-black mt-0 mb-2 transition-up-1 tw-balance" id="title" >
                        {{ article.title }}
                    </h1>

                    <ContentMaterial
                        :modules="article.modules"
                        class="relative mt-5 mt-md-8 transition-up-2"
                    />
                </div>

                <!-- Footer -->
                <div class="article-body mt-8 mx-2 mx-sm-auto mx-lg-0 d-flex flex-column flex-xl-row align-items-center flex-wrap transition-up-3">
                    <time
                        class="color-gray-1"
                        v-if="article.pubDate"
                        :datetime="article.pubDate"
                    >
                        <strong>{{ $t('publishDate') }}:</strong>
                        {{ $timeformat['dd.mm.???? time'](article.pubDate) }}
                    </time>


                    <Stats
                        class="ml-xl-auto mt-2 mt-xl-0"
                        :likes="article.likes"
                        :comments="article.commentsLength"
                        :views="article.views"
                        :realNumbers="true"

                        :liked="article.liked"
                        :modelId="article.id"
                        :modelName="'publication'"

                        @update-likes="(e) => article.likes = e"
                        @update-liked="(e) => article.liked = e"
                    />
                </div>

                <div
                    v-if="tags.length"
                    class="article-body mx-2 mx-sm-auto mx-lg-0 article-tags flex-grow-1 mt-2 mt-xl-3 transition-up-3 ta-center ta-xl-left f-small"
                >
                    <span class="color-gray-0">{{ $t('case.topics') }}:</span>
                    <div
                        class="tag gray"
                        v-for="tag in tags"
                        :key="`article-tag-${tag.id}`"
                    >
                        {{ tag.locales[locale] }}
                    </div>
                </div>


                <div class="delimiter d-md-none mt-7" />


                <div class="mt-0 mt-md-8 mx-sm-auto mx-lg-0 article-body transition-up-3" id="comments">
                    <client-only>
                        <Comments
                            class="article-comments"
                            :modelId="article.id"
                            modelName="publication"
                            :mobileCompact="true"
                            ref="comments"
                            @newReply="newComment =  true"
                        />
                    </client-only>
                </div>
            </div>


            <!-- Side -->
            <div
                class="col-lg-4 transition-up-3 mx-2 mx-lg-0 mt-lg-0 mt-7 d-flex flex-column"
                id="right-side"
                ref="rightSide"
            >
                <div
                    id="static-side"
                    ref="staticSide"
                    class="article-body w-100 mx-sm-auto mx-lg-0"
                >
                    <!-- Company -->
                    <BusinessCard
                        v-if="author"
                        class="mb-3"
                        :profile="author"
                        :followers="followers"
                    />

                    <!-- Ad -->
                    <Banner class="mb-4" :wide="true" />
                </div>

                <div class="flex-grow-1 d-none d-lg-block article-body w-100 mx-sm-auto mx-lg-0">
                    <transition name="fade-top">
                        <div class="sticky-side" v-if="!sideVisible && showFloatingSide">

                            <!-- Company scrolled -->
                            <Card
                                v-if="author"
                                class="article-company compact mb-3 p-2 p-md-3"
                            >
                                <div class="company-badge">
                                    <nuxt-link
                                        :to="localePath(`/${author.slug}`)"
                                        v-profile="{
                                            modelName: author.type,
                                            modelId: author.id,
                                            placement: 'left-start'
                                        }"
                                        class="company-logo"
                                        :class="{user: author.type == 'user'}"
                                    >
                                        <img
                                            :src="author.logo || author.photo"
                                            :alt="author.displayName || author.name"
                                            class="block-image f-shadow"
                                        />
                                    </nuxt-link>

                                    <div class="company-info">
                                        <nuxt-link
                                            :to="localePath(`/${author.slug}`)"
                                            class="my-0 fw-700 d-link d-block l-profile-name"
                                             v-profile="{
                                                modelName: author.type,
                                                modelId: author.id,
                                                placement: 'left-start'
                                            }"
                                        >
                                            {{ author.displayName || author.name }}
                                        </nuxt-link>
                                        <div v-if="author.followers && author.followers > 0" class="f-small color-gray-2">{{author.followers}} {{ $tc('general.followersPlural', author.followers) }}</div>
                                    </div>
                                </div>

                                <div class="company-follow mt-0">
                                    <Follow
                                        class="btn-secondary btn-sm p-0"
                                        :modelName="author.type"
                                        :modelId="author.id"
                                    >
                                        <template v-slot:follow>
                                            <div class="company-follow-button f-small p-h">
                                                <span class="icon-ic-user-add fw-normal" />
                                            </div>
                                        </template>

                                        <template v-slot:following>
                                            <div class="company-follow-button f-small p-h bg-color-rivulet-label">
                                                <span class="icon-ic-tick fw-normal f-10 color-content-bg" />
                                            </div>
                                        </template>
                                    </Follow>
                                </div>
                            </Card>

                            <!-- Ad -->
                            <Banner class="mb-4" :wide="true" />
                        </div>
                    </transition>
                </div>

            </div>
        </div>
    </section>

    <div class="more-content transition-up-3" v-if="moreByAuthor.length">
        <div class="page-container d-flex mt-4 mt-md-8 mb-h align-items-center justify-content-between">
            <h2 class="title-2 h4 h2-md m-0 more-title">
                {{ $t('moreByAuthor.heading') }}
            </h2>

            <nuxt-link
                class="g-link fw-700 f-small d-flex align-items-center more-link"
                :to="localePath(`/${article.company ? article.company.slug : article.author.slug}/feed`)"
            >
                {{ $t('moreByAuthor.link') }} <span class="icon-ic-arrow-simple l-next"/>
            </nuxt-link>
        </div>

        <SideScroll
            class="l-cases center-arrows"
        >
            <Article
                v-for="(article, i) in moreByAuthor"
                :key="`case-${article.id}`"
                class="static scroll-item"
                :article="article"
                @removed="moreByAuthor.splice(i, 1)"
                @hidden="moreByAuthor.splice(i, 1)"
                :class="{
                    'ml-md-3': i != 0,
                    'ml-2': i != 0,
                }"
            />
        </SideScroll>
    </div>

    <!-- Floating actions -->
    <div class="sticky-panel d-flex justify-content-start ml-md-2 mt-3">
        <button
            v-scroll-to="{el: '#title', offset: -100}"
            :class="{'hidden': titleVisible}"
            class="course-scroll-top-button btn-primary d-none d-md-flex"
        >
            <span class="course-scroll-top-icon icon-ic-arrow-down" />
        </button>

        <div class="quick-actions mx-auto ml-md-2 mr-md-0 d-flex f-small">
            <inroom
                v-if="$auth.user && $auth.user.role.fullAccess"
                class="d-none d-md-flex"
                :room="`publication-${article.id}`"
                :wide="true"
                :title="$t('page.article.usersOnline')"
            >
                <span class="fw-normal">{{ $t('page.article.usersOnline') }}</span>
            </inroom>

            <div v-if="$auth.user && $auth.user.role.fullAccess" class="divide mx-3 d-none d-md-block" />

            <Like
                class="btn-quick-action"
                :liked="article.liked"
                :likes="article.likes"
                :modelId="article.id"
                :modelName="'publication'"

                @update-likes="(e) => article.likes = e"
                @update-liked="(e) => article.liked = e"
            />

            <div class="divide mx-3" />

            <button
                v-if="!$auth.user || canCreate"
                @click="writeComment"
                class="btn-quick-action fw-700 relative add-comment"
            >
                <span class="icon-ic-speech-outline" /> {{ $t('comments.addComment') }}

                <transition name="bubble-up">
                    <div class="chip-bage" v-if="newComment"/>
                </transition>
            </button>

            <dd-button
                v-else
                class="btn-quick-action fw-700 relative add-comment"
                name="add-menu"
                anchor="article-add"
                id="article-add"
            >
                <span class="icon-ic-speech-outline" /> {{ $t('comments.addComment') }}
            </dd-button>




            <div v-if="canEdit" class="divide mx-3 d-none d-lg-block" />

            <nuxt-link
                v-if="canEdit"
                :to="localePath(`/editor/${article.id}`)"
                class="btn-quick-action d-none d-lg-block"
                :title="$t('general.edit')"
            >
                <span class="icon-ic-pencil-outline" />
            </nuxt-link>
        </div>
    </div>

    <client-only>
        <Share placement="bottom-end" />
    </client-only>

</section>
</template>

<script>
import Card            from '~/components/cards/Card.vue'
import BusinessCard    from '~/components/profile/BusinessCard.vue'
import ContentMaterial from '~/components/ContentMaterial.vue';
import Stats           from '~/components/reactions/Stats.vue';
import Like            from '~/components/reactions/Like.vue';
import Banner          from '~/components/promo/AsidePromo.vue';
import DdButton        from '~/components/DdButton.vue';
import Follow          from '~/components/profile/Follow.vue';
import Article         from '~/components/thumbnails/NewsThumbnail.vue';
import SideScroll      from '~/components/SideScroll.vue';

import BackButton    from '~/components/BackButton.vue';
import NavigationBar from '~/components/navigation/NavigationBar.vue';
import ShareButton   from '~/components/share/ShareButton.vue';

export default {

    components: {
        Card,
        BusinessCard,
        ContentMaterial,
        Comments: () => import('~/components/comments/Comments.vue'),
        Inroom: () => import('~/components/Inroom.vue'),
        Dropdown: () => import('~/components/CasesDropdown.vue'),
        Stats,
        Like,
        Banner,
        DdButton,
        Follow,
        Article,
        SideScroll,
        BackButton,
        NavigationBar,
        ShareButton,
        Share: () => import('~/components/share/ShareDropdown.vue')
    },

    head () {
        let title = this.article.metaTitle || this.article.title;
        let desc  = this.article.metaDescription || this.article.description;
        let url   = `${process.env.baseUrl}/${this.$route.path}`;
        let image = this.article.thumbnail ? `${process.env.CDN}/image/medium/${this.article.thumbnail.name}` : '';

        let meta = [
            { hid: 'title', name: "title", content: title },

            { hid: "og:url",   property: "og:url",   content: url },
            { hid: "og:title", property: "og:title", content: title },
            { hid: "og:image", property: "og:image", content: image },

            { hid: "twitter:url",   name: "twitter:url",   content: url },
            { hid: "twitter:title", name: "twitter:title", content: title },
            { hid: "twitter:image", name: "twitter:image", content: image }
        ];

        if (desc) {
            meta.push({ hid: 'description',         name: 'description',         content: desc });
            meta.push({ hid: "og:description",      property: "og:description",      content: desc });
            meta.push({ hid: "twitter:description", name: "twitter:description", content: desc });
        }

        let structuredData = {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": this.article.title,
            "datePublished": this.article.createdAt,
            // "dateModified": this.article.updatedAt,
            "author": [{
                "@type": "Organization",
                "name": this.author.name,
                "url": `https://cases.media/${this.author.slug}`,
                "logo": `https://cases.media${this.author.logo}`
            }],
            "mainEntityOfPage": `https://cases.media/news/${this.article.slug || this.article.id}`
        }

        if (this.article.thumbnail) {
            structuredData.image = [
                    `${process.env.CDN}/image/small/${this.article.thumbnail.name}`,
                    `${process.env.CDN}/image/medium/${this.article.thumbnail.name}`,
                    `${process.env.CDN}/image/wide/${this.article.thumbnail.name}`
            ];
        }

        let link = [];
        if (this.article.version) {
            for (let code in this.article.version) {
                link.push({ rel: "alternate", href: this.localePath(`/news/${this.article.version[code].slug}`, code), hreflang: code })
            }
        }

        return {
            title,
            meta,
            link,
            script: [
                {
                    type: 'application/ld+json',
                    json: structuredData
                }
            ]
        }
    },

    data () {
        return {
            article: {},
            author: {},
            followers: 0,
            isModer: false,

            commentsLength: 0,
            tags: [],
            moreByAuthor: [],

            newComment: false,
            titleVisible: true,
            sideVisible: true,
            showFloatingSide: false
        }
    },

    async asyncData ({ $axios, route, error, redirect, app, $appStore }) {
        $appStore.$patch({activeApp: 'media'});
        
        let id = route.params.slug;

        let queries = [
            $axios.$get(`${$axios.defaults.baseURL}/publication/get`,{
                params: {
                    id,
                    type: 'news'
                }
            })
        ];

        let data;
        await Promise.all(queries)
        .then(result => {
            [data] = result;
        })
        .catch(e => {
            error({ statusCode: 404, message: app.i18n.t('page.article.404') });
        });

        let article = data && data.article;

        if (!article) return error({ statusCode: 404, message: app.i18n.t('page.article.404') });

        if (article && article.slug && article.slug != id && !article.isTemp) {
            return redirect(app.localePath(`/news/${article.slug}`));
        }

        if (article.pubDate) article.pubDate = new Date(article.pubDate);

        let result = {
            article,
            author: data.author,
            followers: data.followers,
            moreByAuthor: data.moreByAuthor,

            isModer: data.isModer,
            commentsLength: data.commentsLength,
            tags: data.tags
        };

        return result;
    },

    computed: {
        locale () {
            return this.$i18n.locale || 'uk';
        },

        canEdit () {
            if (!this.$auth.user) return false;
            if (this.$auth.user.role.fullAccess) return true;

            if (this.author.type == 'company') {
                return this.$auth.user.moders && this.$auth.user.moders.some(o => o.approved && o.company.id == this.author.id);
            } else {
                return this.$auth.user.id == this.author.id;
            }
        },

        canCreate () {
            return this.$auth.user && this.$auth.user.role.name.en !== 'Newbie';
        }
    },

    mounted () {
        this.$socket.on('connect', this.connect);
        this.$socket.on(`update`, this.update);

        this.observe();
        this.connect();
        this.checkHeights();

        window.addEventListener('resize', this.checkHeights);

        if (this.article.status !== 'published') {
            let toastText = this.$t('toast.previewText');
            if (this.article.company) {
                toastText = this.$t('toast.previewTextModer', {name: this.author.name});
            }

            this.$toastStore.add({
                id: `preview-${this.article.id}`,
                title: this.$t('toast.previewTitle'),
                text: toastText,
                icon: 'icon-ic-eye',
                autoClose: false
            });
        }
    },

    methods: {
        connect () {
            this.$socket.get(`/user/joinRoom/`, { roomName: `publication-${this.article.id}` });
        },

        update (data) {
            if (data.id != this.article.id || data.model != 'publication') return true;
            if (data.likes) this.$set(this.likes, 'likes', data.likes);
            if (data.views) this.$set(this.article, 'views', data.views);
        },

        writeComment (e) {
            if (!this.$auth.user) {
                this.$modalityStore.openModal({
                    type: 'drawer',
                    name: 'login',
                    message: 'login.401.default'
                });
                return;
            }

            this.$refs.comments.write();
        },


        observe () {
            // Scroll to top observer
            const scrollTopThreshold = 0.3;

            this.titleObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const visible = entry.isIntersecting && entry.intersectionRatio >= scrollTopThreshold;
                    this.titleVisible = visible;
                })
            }, {
                root:       null,
                rootMargin: '0px',
                threshold:  scrollTopThreshold
            });

            const titleBlock = document.querySelector('#title');
            if (titleBlock) this.titleObserver.observe(titleBlock);


            this.sideObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const visible = entry.isIntersecting && entry.intersectionRatio >= 0;
                    this.sideVisible = visible;
                })
            }, {
                root:       null,
                rootMargin: '0px',
                threshold:  0
            });

            const sideBlock = document.querySelector('#static-side');
            if (sideBlock) this.sideObserver.observe(sideBlock);
        },

        unobserve () {
            if (this.titleObserver) this.titleObserver.disconnect();
            if (this.sideObserver) this.sideObserver.disconnect();
        },

        checkHeights () {
            let rightSideHeight = this.$refs.rightSide.offsetHeight;
            let staticSideHeight = this.$refs.staticSide.offsetHeight;
            let size = 500;
            this.showFloatingSide = rightSideHeight - staticSideHeight >= size;
        }
    },

    beforeDestroy () {
        if (process.browser) {
            this.$socket.get(`/user/leaveRoom/`, { roomName: `news-${this.article.id}` });
        }

        this.$socket.off(`update`, this.update);
        this.$socket.off(`connect`, this.connect);
        this.unobserve()
        window.removeEventListener('resize', this.checkHeights)
    }
}
</script>

<style lang="postcss" scoped>
    .l-profile-name {line-height: 1.3;}

    .article-body {
        max-width: 592px;
    }

    .article-tags {
        margin: -2px;

        .tag {
            margin: 2px;
        }
    }

    .f-10 {
        font-size: 10px;
    }

    .relative {
        position: relative;
    }


    .icon-ic-language {font-size: 24px;}
    .btn-quick-action .icon-ic-pencil-outline {font-size: 18px;}

    :deep(.quick-actions) {
        .inroom {
            color: #fff;

            .user.more {
                background-color: #0E1014;
            }
        }
    }


    .fade-top-leave-active ,
    .fade-top-enter-active {
        opacity: 1;
        transform: translateY(0);
        transition: opacity .35s ease,
                    transform .35s ease-out;
    }

    .fade-top-leave-to,
    .fade-top-enter {
        opacity: 0;
        transform: translateY(30px);
        transition: opacity .35s ease,
                    transform .35s ease-in;
    }

    .sticky-side {
        position: sticky;
        top: calc(56px + 40px);
        will-change: transform;
    }

    .article-company {
        display:         flex;
        flex-flow:       row nowrap;
        align-items:     flex-start;
        justify-content: space-between;

        @media screen and (min-width: 768px) {
            &:not(.compact) {
                text-align: center;

                flex-flow:       column nowrap;
                justify-content: flex-start;
                align-items:     stretch;
            }
        }
    }

    .company-badge {
        display:         flex;
        flex-flow:       row nowrap;
        justify-content: flex-start;

        @media screen and (min-width: 768px) {
            flex-flow: column nowrap;

            .article-company.compact & {
                flex-flow: row nowrap;
            }
        }
    }

    .company-logo {
        max-width:  40px;
        max-height: 40px;

        flex: 0 0 auto;

        @media screen and (min-width: 768px) {
            margin: 0 auto 12px;

            flex: 1 0 auto;

            .article-company.compact & {
                margin: 0;

                flex: 0 0 auto;
            }
        }

        @media screen and (min-width: 992px) {
            max-width:  96px;
            max-height: 96px;

            .article-company.compact & {
                max-width:  40px;
                max-height: 40px;
            }
        }

        > img {
            border-radius: 8px;

            @media screen and (min-width: 992px) {
                border-radius: 16px;

                .article-company.compact & {
                    border-radius: 8px;
                }
            }
        }

        &.user > img {border-radius: 50% !important;}
    }

    .company-info {
        margin-left: 12px;

        @media screen and (min-width: 768px) {
            margin-bottom: 16px;
            margin-left:   0;

            .article-company.compact & {
                margin-bottom: 0;
                margin-left:   12px;
            }
        }
    }

    .company-follow {
        display:         flex;
        flex-flow:       row nowrap;
        justify-content: center;

        margin-left: 16px;

        @media screen and (min-width: 768px) {
            margin-left: 0;

            .article-company.compact & {
                margin-left: 16px;
            }
        }
    }

    .company-follow-button {
        min-width:  32px;
        min-height: 32px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;

        @media screen and (min-width: 768px) {
            min-height: 0;

            .article-company.compact & {
                min-height: 32px;
            }
        }
    }

    .l-next {
        transform: rotate(-90deg);
        font-size: 16px;
        margin-left: 4px;
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: .48;
    }

    .nav-bar-title {
        opacity: 0;
        transform: translateY(10px);
        pointer-events: none;
        transition: opacity .15s ease-out,
                    transform .15s ease-out;

        &.show {
            opacity: 1;
            transform: translateY(0);
            pointer-events: all;
        }
    }
</style>
